'use-client';
import React, { useEffect, useImperativeHandle } from 'react';
import type {
  IStylableButtonImperativeActions,
  IStylableButtonProps,
} from '../../../StylableButton/StylableButton.types';
import { useHamburgerMenuContext } from '../../HamburgerMenuContext';
/**
 * Importing semantic classes from HamburgerMenuRoot component because
 * that is the component that gets selected in the editor and
 * it is used to show available CSS classes in Studio editor
 */
import semanticClassNames from '../../HamburgerMenuRoot/HamburgerMenuRoot.semanticClassNames';
import type {
  ClassNames,
  IHamburgerOpenButtonProps,
} from '../HamburgerOpenButton.types';
import classNames from 'classnames';

const HamburgerOpenButton: React.ForwardRefRenderFunction<
  IStylableButtonImperativeActions,
  IHamburgerOpenButtonProps & {
    classes: ClassNames;
    StylableButton: React.ComponentType<IStylableButtonProps>;
  }
> = (
  {
    id,
    a11y = {},
    className,
    classes,
    StylableButton,
    ariaLabel,
    onClick,
    ...props
  },
  ref,
) => {
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const { isMenuOpen, shouldFocus, setIsMenuOpen, prehydrationToggleId } =
    useHamburgerMenuContext();

  useImperativeHandle(ref, () => ({
    focus: () => buttonRef.current?.focus(),
    blur: () => buttonRef.current?.blur(),
  }));

  useEffect(() => {
    if (shouldFocus && isMenuOpen === false) {
      buttonRef.current?.focus();
    }
  }, [shouldFocus, isMenuOpen]);

  const onButtonClick = (event: React.MouseEvent) => {
    setIsMenuOpen(!isMenuOpen);
    onClick?.(event);
  };

  const prehydrationLabel = prehydrationToggleId ? (
    <label
      htmlFor={prehydrationToggleId}
      data-hamburger-btn-label=""
      aria-hidden="true"
    />
  ) : undefined;

  const button = (
    <StylableButton
      {...props}
      className={classNames(
        classes.hamburgerOpenButtonRoot,
        classes.stylableButtonRoot,
        className,
      )}
      a11y={{
        ...a11y,
        expanded: isMenuOpen || false,
        haspopup: 'dialog',
      }}
      id={id}
      onClick={onButtonClick}
      ref={buttonRef}
      semanticClassNames={semanticClassNames}
      prehydrationLabel={prehydrationLabel}
    />
  );

  return (
    <nav aria-label={ariaLabel} className={classes.nav}>
      {button}
    </nav>
  );
};

export default React.forwardRef(HamburgerOpenButton);
